'use client';
import { PlayIcon, PauseIcon, Rewind10SecondsIcon, Forward10SecondsIcon, MuteIcon, UnMuteIcon, TriggerFullScreenIcon } from '@kohler-dsm-library/kohler-icons';
import clsx from 'clsx';
import React, { useRef, useState, useEffect } from 'react';
import { PhotoOverlay } from '../../Foundations/PhotoOverlay/PhotoOverlay.mjs';
import { Flex } from '../../Layout/Flex/Flex.mjs';
import { Box } from '../../Other/Box/Box.mjs';
import classes from './VideoPlayer.module.css.mjs';

const VideoPlayer = (props) => {
  const { className, sources, poster, autoPlay = false, muted = false, ...rest } = props;
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isMuted, setIsMuted] = useState(muted);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const progress = currentTime / duration * 100;
  const togglePlay = () => {
    const video = videoRef.current;
    if (video) {
      if (isPlaying) {
        video.pause();
      } else {
        video.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  const toggleMute = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.addEventListener("loadedmetadata", () => {
        setDuration(video.duration);
      });
      video.addEventListener("timeupdate", () => {
        setCurrentTime(video.currentTime);
      });
    }
  }, []);
  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (video) {
      if (!isFullscreen) {
        video.requestFullscreen();
      } else {
        document.exitFullscreen();
      }
      setIsFullscreen(!isFullscreen);
    }
  };
  const handleVideoProgressUpdate = (e) => {
    const video = videoRef.current;
    if (video) {
      const newPercentage = parseFloat(e.target.value);
      const seekTime = newPercentage * duration;
      video.currentTime = seekTime;
    }
  };
  const handleVideoRewind = () => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = currentTime - 10;
    }
  };
  const handleVideoForward = () => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = currentTime + 10;
    }
  };
  const classNames = clsx(classes.videoWrapper, className, "kohler-video-player-root");
  return /* @__PURE__ */ React.createElement(Box, { className: classNames }, !isPlaying && /* @__PURE__ */ React.createElement(Flex, { component: "button", type: "button", onClick: togglePlay, className: classes.videoOverlayIcon }, /* @__PURE__ */ React.createElement(PlayIcon, null)), /* @__PURE__ */ React.createElement("video", { ref: videoRef, poster, autoPlay: isPlaying, muted: isMuted, className: classes.video, onClick: togglePlay, ...rest }, sources && sources.map((source, index) => /* @__PURE__ */ React.createElement("source", { key: index, src: source.src, type: source.type }))), /* @__PURE__ */ React.createElement(PhotoOverlay, { className: classes.videoControlOverlay, direction: "bottom-top", opacity: 30 }, /* @__PURE__ */ React.createElement(Flex, { className: classes.videoControlsContainer }, /* @__PURE__ */ React.createElement(Flex, { className: classes.videoControlsPlayback }, /* @__PURE__ */ React.createElement(Box, { component: "button", type: "button", onClick: togglePlay }, isPlaying ? /* @__PURE__ */ React.createElement(PauseIcon, { className: classes.videoControlsIcon, "aria-label": "pause video" }) : /* @__PURE__ */ React.createElement(PlayIcon, { className: classes.videoControlsIcon, "aria-label": "play video" })), /* @__PURE__ */ React.createElement(Box, { component: "button", type: "button", onClick: handleVideoRewind, "aria-label": "rewind 10 seconds" }, /* @__PURE__ */ React.createElement(Rewind10SecondsIcon, { className: classes.videoControlsIcon, withBoundingBox: false })), /* @__PURE__ */ React.createElement(Box, { component: "button", type: "button", onClick: handleVideoForward, "aria-label": "forward 10 seconds" }, /* @__PURE__ */ React.createElement(Forward10SecondsIcon, { className: classes.videoControlsIcon, withBoundingBox: false }))), /* @__PURE__ */ React.createElement(Flex, { className: classes.videoControlRangeContainer }, /* @__PURE__ */ React.createElement(
    "input",
    {
      type: "range",
      min: "0",
      max: "1",
      step: "0.01",
      value: progress || 0,
      onChange: handleVideoProgressUpdate,
      className: classes.videoControlRange,
      "aria-label": "video progress control"
    }
  ), /* @__PURE__ */ React.createElement(Box, { className: classes.videoControlRangeProgress, style: { width: `${progress}%` } })), /* @__PURE__ */ React.createElement(Flex, { className: classes.videoControlsVolume }, /* @__PURE__ */ React.createElement(Box, { component: "button", type: "button", onClick: toggleMute }, isMuted ? /* @__PURE__ */ React.createElement(MuteIcon, { className: classes.videoControlsIcon, withBoundingBox: false, "aria-label": "unmute video" }) : /* @__PURE__ */ React.createElement(UnMuteIcon, { className: classes.videoControlsIcon, withBoundingBox: false, "aria-label": "mute video" })), /* @__PURE__ */ React.createElement(Box, { component: "button", type: "button", onClick: toggleFullscreen, "aria-label": "toggle fullscreen" }, /* @__PURE__ */ React.createElement(TriggerFullScreenIcon, { className: classes.videoControlsIcon, withBoundingBox: false }))))));
};

export { VideoPlayer };
